import React from 'react';
import { useNavigate } from 'react-router-dom';

function PrivacyPolicy() {
  const navigate = useNavigate();

  const sections = [
    {
      id: 'intro',
      title: '1. Introduction',
      body: `TradePower ("we," "our," or "us") refers to TradePower Solutions Inc. and is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the TradePower platform.

By using TradePower, you consent to the data practices described in this Privacy Policy. If you do not agree with this policy, please do not use our platform.

This Privacy Policy was last updated on June 1, 2026.`
    },
    {
      id: 'collect',
      title: '2. Information We Collect',
      body: `INFORMATION YOU PROVIDE

We collect information you provide directly to us, including:

- Account information: name, email address, password
- Identity verification: driver's license number, driver's license state, date of birth
- Profile information: photo, phone number, home address, bio, work state
- Trade and professional information: licenses, certifications, hourly rates, service area
- Payment information: bank account or payment details for payouts (processed by our payment provider)
- Communications: messages sent through our platform, reviews, and ratings
- Documents: insurance certificates or other documents uploaded to your profile

INFORMATION WE COLLECT AUTOMATICALLY

When you use TradePower, we automatically collect:

- Log data: IP address, browser type, pages visited, time spent on pages
- Device information: device type, operating system, unique device identifiers
- Location information: approximate location based on IP address
- Usage data: features used, searches performed, interactions with the platform

INFORMATION FROM THIRD PARTIES

We may receive information about you from:

- Background check providers when a background check is conducted
- Payment processors when processing transactions
- Authentication providers if you use Google sign-in`
    },
    {
      id: 'use',
      title: '3. How We Use Your Information',
      body: `We use the information we collect to:

PLATFORM OPERATIONS
- Create and manage your account
- Facilitate connections between Workers and Contractors
- Process bookings, payments, and payouts
- Send job request notifications and platform updates
- Display your profile to other users

SAFETY AND VERIFICATION
- Verify your identity and eligibility
- Conduct or facilitate background checks
- Detect and prevent fraud, abuse, and violations of our Terms
- Investigate and resolve disputes

COMMUNICATIONS
- Send transactional emails (booking confirmations, cancellations, payments)
- Send important platform updates and policy changes
- Respond to your support requests
- With your consent, send marketing communications

IMPROVEMENT
- Analyze usage patterns to improve the platform
- Develop new features and services
- Conduct research and analytics

LEGAL COMPLIANCE
- Comply with applicable laws and regulations
- Respond to legal requests and court orders
- Protect TradePower's legal rights and interests`
    },
    {
      id: 'share',
      title: '4. How We Share Your Information',
      body: `We share your information in the following circumstances:

WITH OTHER USERS
When you use TradePower, certain profile information is visible to other users:
- Workers: name, photo, trades, licenses, ratings, response time, reliability stats, hourly rate, service area
- Contractors: company name, contact name, state, license number, trades, insurance acknowledgment

We do not share your driver's license number, full address, date of birth, or payment information with other users.

WITH SERVICE PROVIDERS
We share information with third-party service providers who assist us in operating the platform, including:
- Payment processors (for transaction processing)
- Background check providers (when authorized)
- Cloud storage and hosting providers
- Analytics providers
- Email and notification services

These providers are contractually bound to use your information only as necessary to provide services to us.

WITH LAW ENFORCEMENT
We may disclose your information if required by law, court order, or government request, or if we believe disclosure is necessary to protect the rights, property, or safety of TradePower, our users, or the public.

BUSINESS TRANSFERS
In the event of a merger, acquisition, or sale of all or substantially all of our assets, your information may be transferred to the acquiring entity.

WE DO NOT SELL YOUR PERSONAL INFORMATION to third parties for their own marketing purposes.`
    },
    {
      id: 'retention',
      title: '5. Data Retention',
      body: `We retain your information for as long as your account is active or as needed to provide our services. Specifically:

- Account information is retained while your account is active and for a reasonable period after deletion to comply with legal obligations
- Transaction records are retained for seven (7) years for tax and legal compliance purposes
- Background check results are retained as required by applicable law and our background check provider's policies
- Messages and communications are retained for twelve (12) months after the associated job is completed
- Reviews and ratings are retained indefinitely as they form part of the platform's integrity and trust system

You may request deletion of your personal information by contacting support@tradepowerapp.com. Note that some information may be retained as required by law or for legitimate business purposes even after account deletion.`
    },
    {
      id: 'security',
      title: '6. Data Security',
      body: `We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction, including:

- Encryption of data in transit using SSL/TLS
- Encryption of sensitive data at rest
- Access controls limiting employee access to user data
- Regular security assessments
- Secure password hashing

However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.

If you believe your account has been compromised, please contact us immediately at support@tradepowerapp.com.

DRIVER'S LICENSE INFORMATION
Driver's license numbers are stored securely and are used solely for identity verification purposes. We display only the last four digits of your license number on your profile and never share your full license number with other users.`
    },
    {
      id: 'rights',
      title: '7. Your Rights and Choices',
      body: `Depending on your location, you may have the following rights regarding your personal information:

ACCESS AND PORTABILITY
You may request a copy of the personal information we hold about you by contacting support@tradepowerapp.com.

CORRECTION
You may update most of your account information directly through your profile settings. For information you cannot update yourself (such as your registered work state or driver's license state), please contact our support team.

DELETION
You may request deletion of your account and personal information. Note that some information may be retained as required by law or for legitimate business purposes, and reviews left by others about you may remain on the platform.

OPT-OUT OF MARKETING
You may opt out of marketing emails by clicking the unsubscribe link in any marketing email or by contacting us directly. Note that you cannot opt out of transactional emails necessary to operate your account.

CALIFORNIA RESIDENTS
California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, the right to delete personal information, and the right to opt out of the sale of personal information. TradePower does not sell personal information.

To exercise any of these rights, please contact us at privacy@tradepowerapp.com.`
    },
    {
      id: 'cookies',
      title: '8. Cookies and Tracking',
      body: `TradePower uses cookies and similar tracking technologies to operate and improve the platform.

ESSENTIAL COOKIES
These cookies are necessary for the platform to function and cannot be disabled. They include session cookies that keep you logged in and security cookies that prevent fraud.

ANALYTICS COOKIES
We use analytics tools to understand how users interact with the platform. This helps us improve features and fix issues. Analytics data is aggregated and does not personally identify you.

THIRD-PARTY COOKIES
Some features of TradePower, such as Google authentication, may set their own cookies. These third parties have their own privacy policies governing their use of cookies.

You can control cookies through your browser settings. Note that disabling certain cookies may affect the functionality of TradePower.`
    },
    {
      id: 'children',
      title: "9. Children's Privacy",
      body: `TradePower is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children under 18.

If we become aware that we have collected personal information from a child under 18, we will take steps to delete that information promptly.

If you are a parent or guardian and believe your child has provided us with personal information, please contact us at privacy@tradepowerapp.com.`
    },
    {
      id: 'changes',
      title: '10. Changes to This Policy',
      body: `We may update this Privacy Policy from time to time. We will notify you of material changes by:

- Posting the updated policy on our platform
- Sending an email notification to your registered email address
- Displaying a notice within the platform

Your continued use of TradePower after any changes indicates your acceptance of the updated Privacy Policy.

We encourage you to review this policy periodically.`
    },
    {
      id: 'contact',
      title: '11. Contact Us',
      body: `If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

TradePower Solutions Inc.
Privacy Team
Email: privacy@tradepowerapp.com
Support: support@tradepowerapp.com

Charlotte, North Carolina

For data deletion requests or to exercise your privacy rights, please email privacy@tradepowerapp.com with the subject line "Privacy Request" and include your account email address.

We will respond to privacy requests within 30 days.`
    },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          TradePower
        </h1>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>← Back</button>
      </div>

      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Privacy Policy</h1>
          <p style={styles.lastUpdated}>Last updated: June 1, 2026</p>
          <p style={styles.intro}>
            Your privacy is important to us. This Privacy Policy explains how TradePower
            Solutions Inc. collects, uses, and protects your personal information.
          </p>
        </div>

        <div style={styles.layout}>
          <div style={styles.toc}>
            <h3 style={styles.tocTitle}>Contents</h3>
            {sections.map(section => (
              <a key={section.id} href={`#${section.id}`} style={styles.tocLink}>
                {section.title}
              </a>
            ))}
            <div style={styles.tocDivider} />
            <a href="/terms" style={styles.tocLinkAlt}
              onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>
              Terms of Service →
            </a>
          </div>

          <div style={styles.sections}>
            {sections.map(section => (
              <div key={section.id} id={section.id} style={styles.section}>
                <h2 style={styles.sectionTitle}>{section.title}</h2>
                <div>
                  {section.body.split('\n\n').map((paragraph, i) => {
                    if (paragraph.startsWith('•')) {
                      const items = paragraph.split('\n').filter(l => l.trim());
                      return (
                        <ul key={i} style={styles.list}>
                          {items.map((item, j) => (
                            <li key={j} style={styles.listItem}>{item.replace('• ', '')}</li>
                          ))}
                        </ul>
                      );
                    }
                    if (paragraph === paragraph.toUpperCase() && paragraph.length < 80) {
                      return <h4 key={i} style={styles.subheading}>{paragraph}</h4>;
                    }
                    return <p key={i} style={styles.paragraph}>{paragraph}</p>;
                  })}
                </div>
              </div>
            ))}

            <div style={styles.footer}>
              <p style={styles.footerText}>
                By using TradePower, you acknowledge that you have read and understood this Privacy Policy.
              </p>
              <div style={styles.footerLinks}>
                <button style={styles.footerBtn} onClick={() => navigate('/terms')}>
                  Terms of Service
                </button>
                <button style={styles.footerBtn} onClick={() => navigate('/dashboard')}>
                  Return to TradePower
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  navbar: {
    backgroundColor: 'white', padding: '16px 32px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', position: 'sticky', top: 0, zIndex: 100,
  },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: {
    padding: '8px 16px', backgroundColor: 'transparent',
    color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
  },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '40px 24px' },
  header: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '32px',
    marginBottom: '24px', textAlign: 'center',
  },
  title: { fontSize: '32px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  lastUpdated: { fontSize: '14px', color: '#999', marginBottom: '16px' },
  intro: { fontSize: '15px', color: '#555', lineHeight: '1.6', maxWidth: '700px', margin: '0 auto' },
  layout: { display: 'grid', gridTemplateColumns: '240px 1fr', gap: '24px', alignItems: 'start' },
  toc: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '20px',
    position: 'sticky', top: '80px',
  },
  tocTitle: {
    fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e',
    marginBottom: '12px', textTransform: 'uppercase',
  },
  tocLink: {
    display: 'block', fontSize: '13px', color: '#555', textDecoration: 'none',
    padding: '5px 0', borderBottom: '1px solid #f0f0f0', lineHeight: '1.4',
  },
  tocLinkAlt: {
    display: 'block', fontSize: '13px', color: '#E94560',
    textDecoration: 'none', padding: '5px 0', fontWeight: 'bold',
  },
  tocDivider: { height: '1px', backgroundColor: '#e0e0e0', margin: '12px 0' },
  sections: { display: 'flex', flexDirection: 'column', gap: '16px' },
  section: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '28px',
  },
  sectionTitle: {
    fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e',
    marginBottom: '16px', borderBottom: '2px solid #E94560', paddingBottom: '8px',
  },
  paragraph: { fontSize: '14px', color: '#444', lineHeight: '1.8', marginBottom: '14px' },
  subheading: {
    fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e',
    marginTop: '16px', marginBottom: '8px', letterSpacing: '0.5px',
  },
  list: { paddingLeft: '20px', marginBottom: '14px' },
  listItem: { fontSize: '14px', color: '#444', lineHeight: '1.8', marginBottom: '4px' },
  footer: {
    backgroundColor: '#1a1a2e', borderRadius: '12px',
    padding: '28px', textAlign: 'center',
  },
  footerText: { fontSize: '14px', color: '#aaa', lineHeight: '1.6', marginBottom: '20px' },
  footerLinks: { display: 'flex', gap: '12px', justifyContent: 'center' },
  footerBtn: {
    padding: '10px 20px', backgroundColor: 'transparent', color: 'white',
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px',
  },
};

export default PrivacyPolicy;